import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, BuildingIcon, StoreIcon, CheckIcon, MapPinIcon } from 'lucide-react';
import { useViewMode, ViewMode, Location } from '../context/ViewModeContext';
export function ViewModeSwitcher() {
  const {
    viewMode,
    setViewMode,
    currentLocation,
    setCurrentLocation,
    locations
  } = useViewMode();
  const [isOpen, setIsOpen] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowLocationPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleModeSelect = (mode: ViewMode) => {
    if (mode === 'store') {
      setShowLocationPicker(true);
    } else {
      setViewMode(mode);
      setIsOpen(false);
    }
  };
  const handleLocationSelect = (location: Location) => {
    setCurrentLocation(location);
    setViewMode('store');
    setShowLocationPicker(false);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1526">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2.5 bg-[#1a1a4a] rounded-lg hover:bg-[#2a2a5a] transition-colors group" data-id="element-1527">
        <div className="flex items-center space-x-3" data-id="element-1528">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${viewMode === 'brand' ? 'bg-purple-500' : 'bg-emerald-500'}`} data-id="element-1529">
            {viewMode === 'brand' ? <BuildingIcon size={16} className="text-white" data-id="element-1530" /> : <StoreIcon size={16} className="text-white" data-id="element-1531" />}
          </div>
          <div className="text-left" data-id="element-1532">
            <p className="text-xs text-gray-400 leading-none" data-id="element-1533">Viewing as</p>
            <p className="text-sm font-semibold text-white leading-tight" data-id="element-1534">
              {viewMode === 'brand' ? 'Brand' : currentLocation?.name || 'Store'}
            </p>
          </div>
        </div>
        <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1535" />
      </button>

      {isOpen && !showLocationPicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-1536">
          <div className="p-2" data-id="element-1537">
            <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1538">
              Switch View
            </p>

            <button onClick={() => handleModeSelect('brand')} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${viewMode === 'brand' ? 'bg-purple-50' : 'hover:bg-gray-50'}`} data-id="element-1539">
              <div className="flex items-center space-x-3" data-id="element-1540">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center" data-id="element-1541">
                  <BuildingIcon size={18} className="text-purple-600" data-id="element-1542" />
                </div>
                <div className="text-left" data-id="element-1543">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1544">
                    Brand View
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1545">
                    All locations & full access
                  </p>
                </div>
              </div>
              {viewMode === 'brand' && <CheckIcon size={18} className="text-purple-600" data-id="element-1546" />}
            </button>

            <button onClick={() => handleModeSelect('store')} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${viewMode === 'store' ? 'bg-emerald-50' : 'hover:bg-gray-50'}`} data-id="element-1547">
              <div className="flex items-center space-x-3" data-id="element-1548">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center" data-id="element-1549">
                  <StoreIcon size={18} className="text-emerald-600" data-id="element-1550" />
                </div>
                <div className="text-left" data-id="element-1551">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1552">
                    Store View
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1553">
                    Single location access
                  </p>
                </div>
              </div>
              {viewMode === 'store' && <CheckIcon size={18} className="text-emerald-600" data-id="element-1554" />}
            </button>
          </div>
        </div>}

      {isOpen && showLocationPicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-1555">
          <div className="p-3 border-b border-gray-100" data-id="element-1556">
            <button onClick={() => setShowLocationPicker(false)} className="text-xs text-blue-600 font-medium hover:underline" data-id="element-1557">
              ← Back to view selection
            </button>
            <p className="text-sm font-semibold text-gray-900 mt-2" data-id="element-1558">
              Select Location
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto p-2" data-id="element-1559">
            {locations.map(location => <button key={location.id} onClick={() => handleLocationSelect(location)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${currentLocation?.id === location.id ? 'bg-emerald-50' : 'hover:bg-gray-50'}`} data-id="element-1560">
                <div className="flex items-center space-x-3" data-id="element-1561">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-1562">
                    <MapPinIcon size={16} className="text-gray-600" data-id="element-1563" />
                  </div>
                  <div className="text-left" data-id="element-1564">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1565">
                      {location.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1566">
                      {location.code} • {location.city}
                    </p>
                  </div>
                </div>
                {currentLocation?.id === location.id && <CheckIcon size={16} className="text-emerald-600" data-id="element-1567" />}
              </button>)}
          </div>
        </div>}
    </div>;
}